import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { reportPaymentUnavailable, useStripeLoadFailure } from '@/public/helpers/stripe'

export function PaymentUnavailableNotice() {
  return (
    <div className="alert alert-warning" role="alert">
      <p className="mb-1"><strong>We couldn&apos;t load our secure payment form.</strong></p>
      <p className="mb-0">
        Refresh the page to try again. If it keeps happening, email
        {' '}
        <a href="mailto:support@rootfunctionalmedicine.com">support@rootfunctionalmedicine.com</a>
        {' '}
        or
        {' '}
        <a href="/contact">send us a message</a>
        , and we&apos;ll help you finish.
      </p>
    </div>
  )
}

// React 17 has no hook for this: only a class component can catch an error
// thrown while its children render or mount. That is how a Stripe Element
// fails -- react-stripe-js mounts it in a layout effect -- and with nothing to
// catch it, React unmounts the whole Loader root around it.
class Boundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { caught: false }
  }

  static getDerivedStateFromError() {
    return { caught: true }
  }

  componentDidCatch(error) {
    const { onError } = this.props
    onError(error)
  }

  render() {
    const { caught } = this.state
    const { fallback, children } = this.props
    return caught ? fallback : children
  }
}

Boundary.propTypes = {
  onError: PropTypes.func.isRequired,
  fallback: PropTypes.node,
  children: PropTypes.node,
}

Boundary.defaultProps = {
  fallback: null,
  children: null,
}

// Stripe-drawn fields, guarded so that a Stripe failure costs only these fields.
// It fails in three ways and each is caught here: Stripe.js never loads, an
// Element reports `loaderror`, or an Element throws. `children` is a function
// handed the `onLoadError` callback to put on each Element.
export default function PaymentFields({
  source,
  fallback,
  onUnavailable,
  children,
}) {
  const loadFailure = useStripeLoadFailure()
  const [fieldFailure, setFieldFailure] = useState(null)
  const failure = loadFailure || fieldFailure
  // Keep the FIRST failure. Every Element here can report its own `loaderror`
  // (the account form has three), and replacing the state on each would re-run
  // the effect below once per Element.
  const recordFailure = useCallback((error) => setFieldFailure((first) => first || error), [])

  useEffect(() => {
    if (!failure) { return }

    reportPaymentUnavailable(source, failure)
    onUnavailable()
  }, [failure])

  return (
    <Boundary fallback={fallback} onError={recordFailure}>
      {failure ? fallback : children((event) => recordFailure(event?.error || new Error('Stripe Element failed to load')))}
    </Boundary>
  )
}

PaymentFields.propTypes = {
  source: PropTypes.string.isRequired,
  fallback: PropTypes.node,
  onUnavailable: PropTypes.func,
  children: PropTypes.func.isRequired,
}

PaymentFields.defaultProps = {
  fallback: <PaymentUnavailableNotice />,
  onUnavailable: () => {},
}
